import { ChangeDetectorRef, Component, computed, ElementRef, inject, input, OnInit, output, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ApiSettings, PaginationConstants } from '../../../../core/constants/constants';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { PaymentMethodType, PaymentStatus } from '../../../../core/enums/data.enums';
import { ModalActions, ModalTypes, PaymentHistoryListViewLocations } from '../../../../core/enums/base.enums';
import { PaymentHistoryListSortOptions } from '../../../../core/enums/sort-options.enums';
import { PaymentHistoryChargeModel, PaymentHistoryModel } from '../../../../core/models/data/payment-history.model';
import { PaymentHistoryFilter } from '../../../../core/filters/payment-history.filter';
import { PropertyModel } from '../../../../core/models/data/property.model';
import { PaginatedListModel } from '../../../../core/models/service/paginated-list.model';
import { ServiceResultModel } from '../../../../core/models/service/service-result.model';
import { PaymentBulkActionRequest } from '../../../../core/models/service/payment-bulk-action-request.model';
import { PaymentService } from '../../../../core/services/payment/payment.service';
import { BalancesMessagingConstants } from '../../../../core/constants/messaging.constants';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { AuthorizationService } from '../../../../core/services/authorization/authorization.service';
import { AuthorizationStateService } from '../../../../core/services/authorization/authorization-state.service';
import { ProfileOrganizationScopeService } from '../../../../core/services/authorization/profile-organization-scope.service';
import { PermissionUtilities } from '../../../../core/utilities/permission.utilities';
import { BaseListPageClass } from '../../../../core/base-classes/base-list-page-class';
import { BalancesPermission, PaymentsPermission, Permission } from '../../../../core/enums/permissions.enums';
import { NoValuePipe } from '../../../../core/pipes/no-value/no-value.pipe';
import { OrgDatePipe } from '../../../../core/pipes/org-date/org-date.pipe';
import { CurrencyDisplayPipe } from '../../../../core/pipes/currency/currency.pipe';
import { MobileCardCheckbox } from '../../../../parts/mobile-card-checkbox/mobile-card-checkbox';
import { StatusBadgeComponent } from '../../../../parts/status-badge/status-badge.component';
import { PaginationComponent } from '../../../../parts/pagination/pagination.component';
import { SortDropdownComponent } from '../../../../parts/sort-dropdown/sort-dropdown/sort-dropdown';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { InViewPortDirective } from '../../../../core/directives/in-view-port.directive';
import { ResponsiveTableDirective } from '../../../../core/directives/responsive-table/responsive-table.directive';
import { ResponsiveTableColumnDirective } from '../../../../core/directives/responsive-table/responsive-table-column.directive';
import { CloseMenuOnSettleDirective } from '../../../../core/directives/close-menu-on-settle.directive';
import { DebounceClickDirective } from '../../../../core/directives/debounce-click.directive';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';
import { FileDownloadUtilities } from '../../../../core/utilities/file-download.utilities';
import { PaymentUtilities } from '../../../../core/utilities/payment.utilities';
import { ApiResponseUtilities } from '../../../../core/utilities/api-response.utilities';
import { IfHasPermissionDirective } from '../../../../core/directives/if-has-permission/if-has-permission.directive';
import { UnmarkPaymentModalComponent } from '../../../balances/components/unmark-payment-modal/unmark-payment-modal.component';
import { RefundPaymentModalComponent } from '../../../balances/components/refund-payment-modal/refund-payment-modal.component';
import { CancelRefundModalComponent } from '../../../balances/components/cancel-refund-modal/cancel-refund-modal.component';

@Component({
	selector: 'ms-payment-history-list',
	imports: [
		CommonModule,
		FormsModule,
		FontAwesomeModule,
		PaginationComponent,
		SortDropdownComponent,
		InViewPortDirective,
		NoValuePipe,
		OrgDatePipe,
		CurrencyDisplayPipe,
		MobileCardCheckbox,
		StatusBadgeComponent,
		ResponsiveTableDirective,
		ResponsiveTableColumnDirective,
		CloseMenuOnSettleDirective,
		DebounceClickDirective,
		TooltipDirective,
		IfHasPermissionDirective,
		InfoModalComponent,
		UnmarkPaymentModalComponent,
		RefundPaymentModalComponent,
		CancelRefundModalComponent,
		ConfirmationModalComponent
	],
	templateUrl: './payment-history-list.component.html',
	styleUrl: './payment-history-list.component.scss',
	host: { "style": "display:contents" }
})
export class PaymentHistoryListComponent extends BaseListPageClass implements OnInit {

	//#region Properties & Signals
	public property = input<PropertyModel | undefined>(undefined);
	public viewLocation = input<PaymentHistoryListViewLocations>(PaymentHistoryListViewLocations.PropertyProfilePage);
	public filter = input<PaymentHistoryFilter | undefined>(undefined);
	public orderByInput = input<string | undefined>(undefined);

	// Set by the profile pages when the viewer owns the property or member record being looked at.
	// Left false on the organization-wide page, which is an admin surface with no owner to speak of.
	public isEntityOwner = input<boolean>(false);

	public orderByChanged = output<string>();

	public paymentHistoryList = signal<PaymentHistoryModel[]>([]);
	public paymentHistoryFilter: PaymentHistoryFilter = ListConstants.PAYMENT_HISTORY_FILTER_DEFAULT();

	public PaymentHistoryListViewLocations = PaymentHistoryListViewLocations;
	public PaymentPermissions = PaymentsPermission;
	public BalancesMessagingConstants = BalancesMessagingConstants;
	public PaymentHistoryListSortOptions = PaymentHistoryListSortOptions;

	public skeletonPayments: PaymentHistoryModel[] = [];
	public expandedPaymentIds = signal<Set<number>>(new Set());
	public downloadingReceiptPaymentId = signal<number | null>(null);
	public resendingReceiptPaymentId = signal<number | null>(null);
	public downloadingStatementPaymentId = signal<number | null>(null);
	public resendingRefundStatementPaymentId = signal<number | null>(null);
	public pendingCancelPayment = signal<PaymentHistoryModel | null>(null);
	public cancelingPaymentId = signal<number | null>(null);
	public isBulkResendingReceipts = signal<boolean>(false);

	// Manual payment IDs are tracked in a sibling signal to selectionItems (Stripe IDs)
	// because paymentId and manualPaymentId are separate identifier spaces and can collide.
	public selectionManualPaymentItems = signal<number[]>([]);

	public isOrgView = computed(() =>
		this.viewLocation() === PaymentHistoryListViewLocations.OrganizationPaymentsHistoryPage
	);

	private profileScope = inject(ProfileOrganizationScopeService, { optional: true });

	// The organization this table belongs to: the profile entity's own when rendered on a member or
	// property profile, otherwise the active one. Member and property profiles are reachable for any
	// organization the viewer belongs to without changing the active one (DEV-2643).
	private scopedOrganizationId = computed<number | undefined>(() =>
		PermissionUtilities.resolveOrganizationScope(undefined, this.profileScope, this.userSessionService).organizationId);

	public showsAllColumns = computed(() =>
		this.isOrgView()
		|| this.viewLocation() === PaymentHistoryListViewLocations.MemberProfilePage
	);

	public isAdminViewer = computed(() => {
		const authState = this.authorizationStateService.authorizationState();
		if (!authState) return false;

		if (this.authorizationService.isStaff(authState)) return true;

		const orgId = this.scopedOrganizationId();
		if (orgId) {
			return this.authorizationService.hasMemberPermission(
				authState.memberPermissions, BalancesPermission.Read, orgId
			);
		}

		return false;
	});

	// Permission gates for the row actions. Resolved here rather than through the permission
	// directives in the template so the Actions dropdown itself can be hidden when every item
	// inside it would be (DEV-2576).
	private canReadPayments = computed(() => this.hasOrganizationPermission(PaymentsPermission.Read));
	private canRefundPayments = computed(() => this.hasOrganizationPermission(PaymentsPermission.Refund));
	private canMarkBalances = computed(() => this.hasOrganizationPermission(BalancesPermission.Mark));
	private canCancelPayments = computed(() => this.hasOrganizationPermission(PaymentsPermission.Cancel));

	// Every member record the viewer holds, across organizations — the same set the Core API checks a
	// payment's payer against when someone without payments.cancel cancels their own payment.
	private viewerMemberIds = computed(() =>
		this.authorizationStateService.authorizationState()?.memberPermissions?.map(mp => mp.memberId) ?? []
	);

	// Someone looking at their own property's or their own record's payments is entitled to their own
	// receipts without holding the org-level payments.read that admins do. That is the same reasoning
	// the profile nav already applies to let them reach this tab at all (ownerBypass in
	// BaseProfilePageClass), and the same entity-owner allowance the disableWithoutPermission
	// directive gave these actions before the checks here were written by hand.
	private canAccessOwnReceipts = computed(() => this.canReadPayments() || this.isEntityOwner());

	private hasActionOnLoadedRow = computed(() =>
		this.paymentHistoryList().some(payment => this.hasAnyRowAction(payment))
	);

	/**
	 * Whether to reserve the Actions column at all — header and cells together.
	 *
	 * Each canShow* in Row Action Visibility is a capability AND a payment state, so a viewer holding
	 * none of the capabilities can have no action on any row: not on the loaded ones, and not on any
	 * page they scroll to later. That makes this answerable from the viewer alone, with no waiting on
	 * results and no column appearing partway down a list. Deliberately *not* driven by whether the
	 * loaded rows happen to have actions — a viewer who can act should keep the column through a page
	 * of Pending payments rather than watch it come and go.
	 *
	 * Every capability guarding a canShow* has to be listed here. hasActionOnLoadedRow is the
	 * failsafe for the day one isn't: it is redundant while that holds, and if it ever stops holding
	 * the column reappears instead of leaving a live action with nowhere to render.
	 */
	public showActionsColumn = computed(() =>
		this.canAccessOwnReceipts()
		|| this.canMarkBalances()
		|| this.canRefundPayments()
		|| this.canCancelPayments()
		|| this.viewerMemberIds().length > 0
		|| this.hasActionOnLoadedRow()
	);

	@ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME().replace(/^#/, '')) jumpToElementRef!: ElementRef;
	@ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME().replace(/^#/, '')) jumpToElementMobileRef!: ElementRef;
	@ViewChild('resendInfoModalRef') resendInfoModalRef!: InfoModalComponent;
	@ViewChild('unmarkPaymentModalRef') unmarkPaymentModalRef!: UnmarkPaymentModalComponent;
	@ViewChild('refundPaymentModalRef') refundPaymentModalRef!: RefundPaymentModalComponent;
	@ViewChild('cancelRefundModalRef') cancelRefundModalRef!: CancelRefundModalComponent;
	@ViewChild('bulkResendReceiptsConfirmationModalRef') bulkResendReceiptsConfirmationModalRef!: ConfirmationModalComponent;
	@ViewChild('bulkResendReceiptsInfoModalRef') bulkResendReceiptsInfoModalRef!: InfoModalComponent;
	@ViewChild('cancelPaymentConfirmationModalRef') cancelPaymentConfirmationModalRef!: ConfirmationModalComponent;

	constructor(
		private paymentService: PaymentService,
		private bootstrapService: BootstrapService,
		private changeDetector: ChangeDetectorRef,
		private authorizationService: AuthorizationService,
		private authorizationStateService: AuthorizationStateService,
		public userSessionService: UserSessionService
	) {
		super();
	}

	public async ngOnInit(): Promise<void> {
		this.sortOptions = [...ListConstants.PAYMENT_HISTORY_SORT_OPTIONS()];

		// Org-wide view receives its filter from the parent page; per-property/member views own their own filter.
		const incomingFilter = this.filter();
		this.paymentHistoryFilter = incomingFilter
			? { ...incomingFilter }
			: ListConstants.PAYMENT_HISTORY_FILTER_DEFAULT();

		this.orderBy = this.orderByInput()
			?? (this.showsAllColumns()
				? ListConstants.PAYMENT_HISTORY_ORG_VIEW_ORDERBY_DEFAULT()
				: ListConstants.PAYMENT_HISTORY_ORDERBY_DEFAULT());

		if (this.property()?.propertyId) {
			this.paymentHistoryFilter.propertyIds = [this.property()!.propertyId];
		}

		// The organization on screen, not the active one — see scopedOrganizationId. On a member or
		// property profile opened for an organization other than the active one, sending the active
		// organization's id alongside the correct memberId/propertyId matched no payments and the
		// Payment History tab rendered empty (DEV-2643).
		const organizationId = this.scopedOrganizationId();
		if (organizationId !== undefined) {
			this.paymentHistoryFilter.organizationIds = [organizationId];
		}

		this.createSkeletonPayments();
		await this.loadPaymentHistory(false);
	}

	private createSkeletonPayments(): void {
		// Succeeded rather than Pending so the placeholder rows carry the same action set as a typical
		// loaded row. The Actions button renders either way now, but a status with no actions renders
		// it disabled, so mismatched skeletons would still have the control change state under the
		// user once real data arrives.
		this.skeletonPayments = Array.from({ length: this.take }, (_, index) => ({
			paymentId: index,
			isManualPayment: false,
			status: PaymentStatus.Succeeded,
			paidDate: '2025-01-01',
			latestDueDate: '2025-01-01',
			amount: 0,
			fees: 0,
			paymentMethodType: PaymentMethodType.Card,
			currency: { currencyId: 1, name: 'USD', symbol: '$', minorUnits: 100 },
			charges: [{
				propertyChargeId: index,
				name: 'Loading...',
				dueDate: '2025-01-01',
				amount: 0,
				fees: 0,
				memberPaidServiceFee: false,
				propertyId: -1
			}]
		} as PaymentHistoryModel));
	}
	//#endregion Properties & Signals

	//#region Data Loading
	public get displayPayments(): PaymentHistoryModel[] {
		if (this.isLoading && (!this.paymentHistoryList() || this.paymentHistoryList().length === 0)) {
			return this.skeletonPayments;
		}
		return this.paymentHistoryList();
	}

	public async loadPaymentHistory(append: boolean = false): Promise<void> {
		this.isLoading = true;

		if (append === false) { this.resetPagination(); }

		// In org view the parent page owns the filter; refresh from the input on each load.
		if (this.isOrgView()) {
			const incomingFilter = this.filter();
			if (incomingFilter) {
				this.paymentHistoryFilter = { ...incomingFilter };
			}
		}

		const orgSettings = await this.userSessionService.getOrganizationSettings();
		this.paymentHistoryFilter.timeZoneId =
			orgSettings?.timeZoneId ?? ApiSettings.DEFAULT_ORG_TIMEZONE_FALLBACK().id;

		const response: ServiceResultModel<PaginatedListModel<PaymentHistoryModel>> =
			await this.paymentService.searchPaymentHistory(this.paymentHistoryFilter, this.orderBy, this.skip(), this.take);

		this.totalCount = response?.data?.total ?? 0;
		this.hasNext = response?.data?.hasNext ?? false;

		if (response?.data?.data) {
			if (append === true) {
				this.paymentHistoryList.set(this.paymentHistoryList().concat(response.data.data));
			} else {
				this.paymentHistoryList.set(response.data.data);
				this.isSelectAll = false;
				this.selectionItems.set([]);
				this.selectionManualPaymentItems.set([]);
			}
		}

		this.isLoading = false;
		this.calculateSelected();
		this.changeDetector.detectChanges();
	}

	public async loadMore(): Promise<void> {
		await this.loadPaymentHistory(true);
	}

	public filterApplied(): void {
		this.loadPaymentHistory();
	}

	public loadMoreInView(): void {
		if (this.canLoadMore && this.hasNext) {
			this.loadMore();
			this.canLoadMore = false;
			this.loadMoreDebounce();
		}
	}
	//#endregion Data Loading

	//#region Sorting & Pagination
	public toggleSortDirection(columnName: string): void {
		const columnSortMap: Record<string, [PaymentHistoryListSortOptions, PaymentHistoryListSortOptions]> = {
			"Status": [PaymentHistoryListSortOptions.StatusAsc, PaymentHistoryListSortOptions.StatusDesc],
			"Date Paid": [PaymentHistoryListSortOptions.DatePaidAsc, PaymentHistoryListSortOptions.DatePaidDesc],
			"Due Date": [PaymentHistoryListSortOptions.DueDateAsc, PaymentHistoryListSortOptions.DueDateDesc],
			"Amount": [PaymentHistoryListSortOptions.AmountAsc, PaymentHistoryListSortOptions.AmountDesc],
			"Fees": [PaymentHistoryListSortOptions.FeesAsc, PaymentHistoryListSortOptions.FeesDesc]
		};

		const [ascOption, descOption] = columnSortMap[columnName]
			|| [PaymentHistoryListSortOptions.DatePaidAsc, PaymentHistoryListSortOptions.DatePaidDesc];

		let sortDirection: PaymentHistoryListSortOptions;

		if (this.orderBy === descOption) {
			sortDirection = ascOption;
		} else if (this.orderBy === ascOption) {
			sortDirection = descOption;
		} else {
			sortDirection = ascOption;
		}

		this.setSortOrder(sortDirection);
	}

	public setSortOrder(sortOption: string) {
		this.orderBy = sortOption;
		this.orderByChanged.emit(sortOption);
		this.filterApplied();
	}

	public jumpToTop(element: string): void {
		switch (element) {
			case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME():
				this.jumpToElementRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
				break;

			case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME():
				this.jumpToElementMobileRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
				break;
		}
	}
	//#endregion Sorting & Pagination

	//#region Actions
	// Drives msCloseMenuOnSettle: a row's menu offers four actions, so whichever one runs closes it.
	public isPaymentActionRunning(payment: PaymentHistoryModel): boolean {
		const effectiveId = PaymentUtilities.getEffectivePaymentId(payment);

		return this.downloadingReceiptPaymentId() === effectiveId
			|| this.resendingReceiptPaymentId() === effectiveId
			|| this.downloadingStatementPaymentId() === payment.paymentId
			|| this.resendingRefundStatementPaymentId() === payment.paymentId;
	}

	public async downloadReceipt(payment: PaymentHistoryModel): Promise<void> {
		if (this.downloadingReceiptPaymentId() !== null) return;

		const effectiveId = PaymentUtilities.getEffectivePaymentId(payment);
		this.downloadingReceiptPaymentId.set(effectiveId);

		try {
			const response = await this.paymentService.downloadReceipt(effectiveId, payment.isManualPayment);

			if (response.success && response.data) {
				const filename = FileDownloadUtilities.getFilenameFromContentDisposition(response.message ?? null)
					|| `Receipt_${effectiveId}.pdf`;
				FileDownloadUtilities.downloadBlob(response.data, filename);
			}
		} finally {
			this.downloadingReceiptPaymentId.set(null);
		}
	}

	public async resendReceipt(payment: PaymentHistoryModel): Promise<void> {
		if (this.resendingReceiptPaymentId() !== null) return;

		const effectiveId = PaymentUtilities.getEffectivePaymentId(payment);
		this.resendingReceiptPaymentId.set(effectiveId);

		try {
			const response = await this.paymentService.resendReceipt(effectiveId, payment.isManualPayment);
			this.showResendResult(
				response.success === true,
				response.message ?? BalancesMessagingConstants.RESEND_RECEIPT_SUCCESS(),
				response.message ?? BalancesMessagingConstants.RESEND_RECEIPT_FAILURE()
			);
		} finally {
			this.resendingReceiptPaymentId.set(null);
		}
	}

	private showResendResult(success: boolean, successMessage: string, failureMessage: string): void {
		this.resendInfoModalRef.modalType.set(success ? ModalTypes.Success : ModalTypes.Failure);
		this.resendInfoModalRef.message.set(success ? successMessage : failureMessage);
		this.bootstrapService.manageModal('#paymentResendInfoModal', ModalActions.Show);
	}

	// Sends on the click: a single resend takes no confirmation, unlike the bulk one (DEV-2806).
	public async resendRefundStatement(payment: PaymentHistoryModel): Promise<void> {
		// Refunded payments always have a Stripe paymentId (template gates on !isManualPayment),
		// but the model types paymentId as optional — guard explicitly for TS.
		if (payment.paymentId === undefined || this.resendingRefundStatementPaymentId() !== null) return;

		const paymentId = payment.paymentId;
		this.resendingRefundStatementPaymentId.set(paymentId);

		try {
			const response = await this.paymentService.resendRefundStatement(paymentId);

			this.showResendResult(
				response.success === true,
				response.message ?? BalancesMessagingConstants.RESEND_REFUND_STATEMENT_SUCCESS(),
				response.message ?? BalancesMessagingConstants.RESEND_REFUND_STATEMENT_FAILURE()
			);
		} finally {
			this.resendingRefundStatementPaymentId.set(null);
		}
	}

	public override toggleSelectAll(): void {
		this.isSelectAll = !this.isSelectAll;
		this.selectionItems.set([]);
		this.selectionManualPaymentItems.set([]);
		this.calculateSelected();
	}

	public paymentSelectionClicked(payment: PaymentHistoryModel, checked: boolean): void {
		const id = PaymentUtilities.getEffectivePaymentId(payment);
		if (!id) { return; }

		const targetSignal = payment.isManualPayment
			? this.selectionManualPaymentItems
			: this.selectionItems;

		if (this.isSelectAll) {
			// isSelectAll true — track *deselected* IDs
			if (!checked && !targetSignal().includes(id)) {
				targetSignal.update(items => [...items, id]);
			} else {
				targetSignal.update(items => items.filter(x => x !== id));
			}
		} else {
			// isSelectAll false — track *selected* IDs
			if (checked && !targetSignal().includes(id)) {
				targetSignal.update(items => [...items, id]);
			} else {
				targetSignal.update(items => items.filter(x => x !== id));
			}
		}

		this.calculateSelected();
	}

	public paymentIsSelected(payment: PaymentHistoryModel): boolean {
		const id = PaymentUtilities.getEffectivePaymentId(payment);
		if (!id) { return false; }

		const targetSignal = payment.isManualPayment
			? this.selectionManualPaymentItems
			: this.selectionItems;

		const contains = targetSignal().includes(id);
		return this.isSelectAll ? !contains : contains;
	}

	public override calculateSelected(): void {
		const trackedCount = this.selectionItems().length + this.selectionManualPaymentItems().length;
		if (this.isSelectAll) {
			this.selectedCount.set(this.totalCount - trackedCount);
		} else {
			this.selectedCount.set(trackedCount);
		}
	}

	public resendSelectedReceipts(): void {
		if (this.selectedCount() === 0 || this.isBulkResendingReceipts()) { return; }

		const message = BalancesMessagingConstants.BULK_RESEND_RECEIPTS_CONFIRMATION()
			.replace('{selectedCount}', String(this.selectedCount()));

		this.bulkResendReceiptsConfirmationModalRef.modalType.set(ModalTypes.Question);
		this.bulkResendReceiptsConfirmationModalRef.title.set('Resend Receipts');
		this.bulkResendReceiptsConfirmationModalRef.message.set(message);
		this.bulkResendReceiptsConfirmationModalRef.enableNotes.set(false);
		this.bulkResendReceiptsConfirmationModalRef.confirmButtonText.set('Confirm');

		this.bootstrapService.manageModal('#bulkResendReceiptsConfirmationModal', ModalActions.Show);
	}

	public async onBulkResendReceiptsConfirmed(): Promise<void> {
		if (this.isBulkResendingReceipts()) { return; }
		this.isBulkResendingReceipts.set(true);
		this.bulkResendReceiptsConfirmationModalRef.isSubmitting.set(true);

		try {
			const request = this.buildPaymentBulkActionRequest();
			const response = await this.paymentService.bulkResendReceipts(request);

			this.bootstrapService.manageModal('#bulkResendReceiptsConfirmationModal', ModalActions.Hide);

			if (response.success) {
				this.bulkResendReceiptsInfoModalRef.modalType.set(ModalTypes.Success);
				this.bulkResendReceiptsInfoModalRef.message.set(
					response.message ?? BalancesMessagingConstants.BULK_RESEND_RECEIPTS_SUCCESS()
				);
				this.isSelectAll = false;
				this.selectionItems.set([]);
				this.selectionManualPaymentItems.set([]);
				this.calculateSelected();
			} else {
				this.bulkResendReceiptsInfoModalRef.modalType.set(ModalTypes.Failure);
				this.bulkResendReceiptsInfoModalRef.message.set(
					response.message ?? BalancesMessagingConstants.BULK_RESEND_RECEIPTS_FAILURE()
				);
			}
			this.bootstrapService.manageModal('#bulkResendReceiptsInfoModal', ModalActions.Show);
		} finally {
			this.isBulkResendingReceipts.set(false);
			this.bulkResendReceiptsConfirmationModalRef.isSubmitting.set(false);
		}
	}

	public onBulkResendReceiptsCancelled(): void {
		this.bootstrapService.manageModal('#bulkResendReceiptsConfirmationModal', ModalActions.Hide);
	}

	private buildPaymentBulkActionRequest(): PaymentBulkActionRequest {
		if (this.isSelectAll) {
			return {
				useFilter: true,
				filter: this.paymentHistoryFilter,
				selectedIds: null,
				excludedIds: this.selectionItems().length > 0 ? [...this.selectionItems()] : null,
				selectedManualPaymentIds: null,
				excludedManualPaymentIds: this.selectionManualPaymentItems().length > 0
					? [...this.selectionManualPaymentItems()]
					: null,
				note: null
			};
		} else {
			return {
				useFilter: false,
				filter: null,
				selectedIds: this.selectionItems().length > 0 ? [...this.selectionItems()] : null,
				excludedIds: null,
				selectedManualPaymentIds: this.selectionManualPaymentItems().length > 0
					? [...this.selectionManualPaymentItems()]
					: null,
				excludedManualPaymentIds: null,
				note: null
			};
		}
	}

	public openUnmarkModal(payment: PaymentHistoryModel): void {
		const chargeId = payment.charges?.[0]?.propertyChargeId;
		if (chargeId === undefined) return;
		this.unmarkPaymentModalRef.open(chargeId, payment);
	}

	public onUnmarkCompleted(): void {
		this.loadPaymentHistory(false);
	}

	public openRefundModal(payment: PaymentHistoryModel): void {
		if (payment.paymentId === undefined) return;
		// The filter's organization is the scoped one set in ngOnInit; the fallback resolves the same
		// way rather than reaching for the active organization, so a refund is never issued against an
		// organization other than the one the payment belongs to (DEV-2643).
		const orgId = this.paymentHistoryFilter.organizationIds?.[0] ?? this.scopedOrganizationId();
		this.refundPaymentModalRef.open(payment, orgId);
	}

	public onRefundCompleted(): void {
		this.loadPaymentHistory(false);
	}

	public openCancelRefundModal(payment: PaymentHistoryModel): void {
		if (payment.paymentId === undefined) return;
		this.cancelRefundModalRef.open(payment);
	}

	public onCancelRefundCompleted(): void {
		this.loadPaymentHistory(false);
	}

	public openCancelPaymentModal(payment: PaymentHistoryModel): void {
		if (payment.paymentId === undefined) return;
		if (this.cancelingPaymentId() !== null) return;

		this.pendingCancelPayment.set(payment);

		this.cancelPaymentConfirmationModalRef.modalType.set(ModalTypes.Question);
		this.cancelPaymentConfirmationModalRef.title.set('Cancel Payment');
		this.cancelPaymentConfirmationModalRef.message.set(BalancesMessagingConstants.CANCEL_PAYMENT_CONFIRMATION());
		this.cancelPaymentConfirmationModalRef.enableNotes.set(false);
		this.cancelPaymentConfirmationModalRef.confirmButtonText.set('Cancel Payment');
		this.cancelPaymentConfirmationModalRef.confirmButtonClassName.set('btn-danger');
		this.cancelPaymentConfirmationModalRef.cancelButtonText.set('Go Back');

		this.bootstrapService.manageModal('#cancelPaymentConfirmationModal', ModalActions.Show);
	}

	public async onCancelPaymentConfirmed(): Promise<void> {
		const payment = this.pendingCancelPayment();
		if (!payment || payment.paymentId === undefined || this.cancelingPaymentId() !== null) return;

		const paymentId = payment.paymentId;
		this.cancelingPaymentId.set(paymentId);
		this.cancelPaymentConfirmationModalRef.isSubmitting.set(true);

		try {
			const response = await this.paymentService.cancelPayment(paymentId);

			this.bootstrapService.manageModal('#cancelPaymentConfirmationModal', ModalActions.Hide);

			if (response.success) {
				// The row moves to Canceling in place; the caching interceptor has already evicted the
				// cached payment lists, so the next load reads the new status from the server.
				const status = response.data?.status ?? PaymentStatus.Canceling;
				this.paymentHistoryList.update(list =>
					list.map(p => !p.isManualPayment && p.paymentId === paymentId ? { ...p, status } : p)
				);
			}

			this.showResendResult(
				response.success === true,
				BalancesMessagingConstants.CANCEL_PAYMENT_SUCCESS(),
				response.message ?? BalancesMessagingConstants.CANCEL_PAYMENT_FAILURE()
			);

			// 409: the payment is no longer Pending (canceled elsewhere, or already settled), so the row is
			// showing a stale status. A 422 (card payment, or Stripe has already submitted the debit to the
			// bank) leaves the payment Pending, which is what the row already says.
			if (ApiResponseUtilities.isConflictStatusCode(response.statusCode)) {
				this.loadPaymentHistory(false);
			}
		} finally {
			this.cancelingPaymentId.set(null);
			this.pendingCancelPayment.set(null);
			this.cancelPaymentConfirmationModalRef.isSubmitting.set(false);
		}
	}

	public onCancelPaymentCancelled(): void {
		this.bootstrapService.manageModal('#cancelPaymentConfirmationModal', ModalActions.Hide);
		this.pendingCancelPayment.set(null);
	}

	public async downloadRefundStatement(payment: PaymentHistoryModel): Promise<void> {
		if (this.downloadingStatementPaymentId() !== null) return;
		if (payment.paymentId === undefined) return;

		this.downloadingStatementPaymentId.set(payment.paymentId);

		try {
			const response = await this.paymentService.downloadRefundStatement(payment.paymentId);

			if (response.success && response.data) {
				const filename = FileDownloadUtilities.getFilenameFromContentDisposition(response.message ?? null)
					|| `RefundStatement_${payment.paymentId}.pdf`;
				FileDownloadUtilities.downloadBlob(response.data, filename);
			}
		} finally {
			this.downloadingStatementPaymentId.set(null);
		}
	}
	//#endregion Actions

	//#region Row Action Visibility
	// Which row actions a viewer is offered depends on the payment's status, on whether they are an
	// admin/staff viewer or a member looking at their own payments, and on which table they are
	// looking at. An action the viewer isn't permitted to take is hidden rather than greyed out
	// (DEV-2576).
	//
	// A member reading their own payment history gets the documents they might want and nothing else:
	// Download Receipt on a settled payment, Download Refund Statement on a refunded one. Everything
	// that alters a payment or mails something out on the organization's behalf needs the matching
	// permission, which members do not hold — the split is by what the viewer can do, not by which
	// page they are on. The one exception is Cancel Payment on a Pending ACH payment the member made
	// themselves (DEV-1866). A member reaches these tables through the profile nav's owner bypass, so the
	// receipt downloads accept that ownership (canAccessOwnReceipts) while everything else keeps
	// asking for the permission itself. Note this is deliberately never phrased as "is an admin":
	// isAdminViewer() reads balances.read, which would wrongly exclude a role granted payments.read
	// on its own.
	//
	// The button holding those actions is treated the other way round on the desktop table: it always
	// renders — disabled, and explaining itself by tooltip — on statuses with nothing actionable
	// (card or uncancelable Pending, Canceling, Failed, Canceled, Unmarked), because the table reserves a column for it and a cell
	// that is sometimes empty and sometimes not reads as broken. Mobile cards have no column to keep
	// straight, so there the button is dropped rather than leaving a dead full-width bar on the card.

	private hasOrganizationPermission(permission: Permission): boolean {
		const authorizationState = this.authorizationStateService.authorizationState();
		if (!authorizationState) return false;

		return PermissionUtilities.hasScopedOrganizationPermission(
			permission, authorizationState, this.authorizationService, this.profileScope, this.userSessionService
		);
	}

	// A receipt only exists once the payment has gone through, so it is offered on succeeded and
	// manually marked payments. A refund in flight withdraws it from the profile tables entirely; the
	// organization-wide page still offers it to admins there, where the original payment having
	// settled is the fact that matters.
	public canShowDownloadReceipt(payment: PaymentHistoryModel): boolean {
		if (!this.canAccessOwnReceipts()) return false;

		switch (payment.status) {
			case PaymentStatus.Succeeded:
			case PaymentStatus.Marked:
				return true;
			case PaymentStatus.RefundPending:
				return this.canReadPayments();
			default:
				return false;
		}
	}

	// Mailing a payer a copy of their own receipt is an organization-side action: a member reading
	// their own payment history already has the document in front of them, so they get the download
	// and nothing else. Admins and staff keep it wherever they are working (DEV-2576).
	public canShowResendReceipt(payment: PaymentHistoryModel): boolean {
		return this.canReadPayments()
			&& (payment.status === PaymentStatus.Succeeded || payment.status === PaymentStatus.Marked);
	}

	public canShowUnmarkPayment(payment: PaymentHistoryModel): boolean {
		return this.canMarkBalances() && payment.status === PaymentStatus.Marked;
	}

	public canShowDownloadRefundStatement(payment: PaymentHistoryModel): boolean {
		return this.canAccessOwnReceipts()
			&& !payment.isManualPayment
			&& payment.status === PaymentStatus.Refunded;
	}

	// Resending the statement mails the member a copy, so it is an admin-side action even though
	// the member may download the same document themselves.
	public canShowResendRefundStatement(payment: PaymentHistoryModel): boolean {
		return this.canReadPayments() && this.canShowDownloadRefundStatement(payment);
	}

	public canShowCancelRefund(payment: PaymentHistoryModel): boolean {
		return this.canRefundPayments()
			&& !payment.isManualPayment
			&& payment.status === PaymentStatus.RefundPending
			&& payment.paymentMethodType === PaymentMethodType.AchDebit;
	}

	// The one action a member may take on their own payment without a permission: canceling it while
	// it is still a Pending ACH debit. See PaymentUtilities.canCancelPayment (DEV-1866).
	public canShowCancelPayment(payment: PaymentHistoryModel): boolean {
		return PaymentUtilities.canCancelPayment(payment, this.canCancelPayments(), this.viewerMemberIds());
	}

	public canShowRefundPayment(payment: PaymentHistoryModel): boolean {
		return this.canRefundPayments()
			&& !payment.isManualPayment
			&& payment.status === PaymentStatus.Succeeded;
	}

	// The receipt actions sit at the top of the dropdown; the groups below them only warrant a
	// divider when at least one of these rendered above.
	public hasReceiptActions(payment: PaymentHistoryModel): boolean {
		return this.canShowDownloadReceipt(payment) || this.canShowResendReceipt(payment);
	}

	public hasAnyRowAction(payment: PaymentHistoryModel): boolean {
		return this.hasReceiptActions(payment)
			|| this.canShowUnmarkPayment(payment)
			|| this.canShowDownloadRefundStatement(payment)
			|| this.canShowResendRefundStatement(payment)
			|| this.canShowCancelRefund(payment)
			|| this.canShowCancelPayment(payment)
			|| this.canShowRefundPayment(payment);
	}
	//#endregion Row Action Visibility

	//#region Helper Methods
	public hasMultipleCharges(payment: PaymentHistoryModel): boolean {
		return (payment.charges?.length ?? 0) > 1;
	}

	public isExpanded(payment: PaymentHistoryModel): boolean {
		return this.expandedPaymentIds().has(this.getEffectivePaymentId(payment));
	}

	public toggleAccordion(payment: PaymentHistoryModel): void {
		const id = this.getEffectivePaymentId(payment);
		const current = new Set(this.expandedPaymentIds());

		if (current.has(id)) {
			current.delete(id);
		} else {
			current.add(id);
		}

		this.expandedPaymentIds.set(current);
	}

	public getEffectivePaymentId(payment: PaymentHistoryModel): number {
		return PaymentUtilities.getEffectivePaymentId(payment);
	}

	public getChargeNameDisplay(payment: PaymentHistoryModel): string {
		return PaymentUtilities.getChargeNameDisplay(payment);
	}

	public getSelectionAriaLabel(payment: PaymentHistoryModel): string {
		const name = this.getChargeNameDisplay(payment);
		return name ? `Select ${name}` : 'Select payment';
	}

	public getInvoiceNumberDisplay(payment: PaymentHistoryModel): string {
		return PaymentUtilities.getInvoiceNumberDisplay(payment);
	}

	public getLatestDueDate(payment: PaymentHistoryModel): string {
		if ((payment?.charges?.length ?? 0) > 1) {
			return "";
		} else {
			return payment.latestDueDate;
		}
	}

	public getDisplayFee(payment: PaymentHistoryModel): number {
		if (this.isAdminViewer()) {
			return payment.fees;
		}

		const memberPays = payment.charges?.some(c => c.memberPaidServiceFee) ?? false;
		return memberPays ? payment.fees : 0;
	}

	public getPaymentMethodDisplay(payment: PaymentHistoryModel): string {
		return PaymentUtilities.formatPaymentMethod(payment);
	}

	public hasMultipleProperties(payment: PaymentHistoryModel): boolean {
		return this.getDistinctPropertyCount(payment) > 1;
	}

	public getDistinctPropertyCount(payment: PaymentHistoryModel): number {
		const charges = payment.charges ?? [];
		if (charges.length === 0) return 0;
		return new Set(charges.map(c => c.propertyId)).size;
	}

	public getFirstCharge(payment: PaymentHistoryModel): PaymentHistoryChargeModel | undefined {
		return payment.charges?.[0];
	}
	//#endregion Helper Methods
}
